/* tslint:disable:max-line-length */
import {Injectable, OnDestroy} from '@angular/core';
import {Activity} from '@docma-mm-core/activity/activity';
import {Attachment, AttachmentSource} from '@docma-mm-core/attachment/attachment';
import {AttachmentLoadingTypeEnum} from '@docma-mm-core/attachment/attachment-loading-type.enum';
import {AttachmentService} from '@docma-mm-core/attachment/attachment.service';
import {Fault, ViewFault} from '@docma-mm-core/fault/fault';
import {FaultService} from '@docma-mm-core/fault/fault.service';
import {FaultSyncService, SyncStateChange} from '@docma-mm-core/fault/sync/fault-sync.service';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import {Project} from '@docma-mm-core/project/project';
import {PendingService} from '@mydocma/app-container';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {TranslateService} from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, filter, firstValueFrom, map, Subject} from 'rxjs';
import {MmViewService} from '../mm-view.service';
import {MmFaultsLoaderService} from './mm-faults-loader.service';
import {User} from '@docma-mm-core/user/user';
import {FaultPersistenceService} from '@docma-mm-core/fault/fault-persistence.service';
import {LoadLayoutFaultsOptions} from './load-layout-faults-options.enum';
import {FieldPropertyEnum} from '@docma-mm-core/field/field-property.enum';
import {MmProjectLoaderService} from '../mm-project/mm-project-loader.service';
import {ProjectService} from '@mydocma/project';
import {ErrorService} from './mm-error.service';
import {Logger} from '@mydocma/common/src/lib/logger';

/** Interface for field name mappings */
interface FieldNameMappings {
  byProp: Record<string, string>;
  byDefaultName: Record<string, string>;
}

/** Interface for categorized sync state changes */
interface CategorizedSyncChanges {
  syncing: SyncStateChange[];
  error: SyncStateChange[];
  synced: SyncStateChange[];
}

@UntilDestroy()
@Injectable()
export class MmFaultsSyncService implements OnDestroy {

  private readonly syncFaultSubjectsMap = new Map<string, BehaviorSubject<boolean>>();
  private project: Project;
  private readonly syncNewFaultSubject = new Subject<ViewFault>();
  private readonly syncFaultSubject = new Subject<ViewFault>();

  public readonly onNewFaultSynced = this.syncNewFaultSubject.asObservable();
  public readonly onFaultSynced = this.syncFaultSubject.asObservable();

  /** Cached field name mappings */
  private fieldMappings: FieldNameMappings = { byProp: {}, byDefaultName: {} };

  constructor(
    private readonly faultSyncService: FaultSyncService,
    private readonly pendingService: PendingService,
    private readonly attachmentService: AttachmentService,
    private readonly viewService: MmViewService,
    private readonly faultService: FaultService,
    private readonly notificationService: NotificationService,
    private readonly faultsLoaderService: MmFaultsLoaderService,
    private readonly persistenceService: FaultPersistenceService,
    private readonly translateService: TranslateService,
    private readonly projectLoaderService: MmProjectLoaderService,
    private readonly projectService: ProjectService,
    private readonly errorService: ErrorService,
    private readonly logger: Logger
  ) {
    this.initializeSubscriptions();
  }

  ngOnDestroy(): void {
    this.syncFaultSubjectsMap.clear();
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  /** Initialize all subscriptions */
  private initializeSubscriptions(): void {
    this.projectService.currentProject
      .pipe(untilDestroyed(this))
      .subscribe(() => this.initFields());

    this.viewService.currentProject$
      .pipe(untilDestroyed(this))
      .subscribe(p => this.project = p);

    this.faultSyncService.syncFaultsState$
      .pipe(untilDestroyed(this))
      .subscribe(s => this.onFaultsSyncStateChanged(s));

    this.onFaultSynced
      .pipe(untilDestroyed(this))
      .subscribe(fault => this.cleanupPendingAttachmentDeletes(fault));
  }

  /** Initialize field name mappings (cached for performance) */
  async initFields(): Promise<void> {
    try {
      const fields = await firstValueFrom(
        this.projectLoaderService.fields.pipe(
          filter(f => f.loadState.status === LoadStatusEnum.Loaded),
          map(m => m.data.filter(x => x.Id != null))
        )
      );

      this.fieldMappings = {
        byProp: Object.fromEntries(fields.map(x => [x.Prop, x.Project ?? x.Global])),
        byDefaultName: Object.fromEntries(fields.map(x => [x.Id, x.Project ?? x.Global]))
      };
    } catch {
      this.fieldMappings = { byProp: {}, byDefaultName: {} };
    }
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  async syncFaults(faults: ViewFault[]): Promise<void> {
    if (faults.length === 0) {
      return;
    }

    const faultsToLoad = faults.filter(f => f.loadState.status !== LoadStatusEnum.NotLoaded);
    const layoutFaultsToLoad = faults.filter(f => f.layoutState.loadState.status !== LoadStatusEnum.NotLoaded);

    // Load in parallel
    await Promise.all([
      this.faultsLoaderService.loadFaults(faultsToLoad, true),
      this.faultsLoaderService.loadLayoutFaults(layoutFaultsToLoad, true, false, LoadLayoutFaultsOptions.EnsureVisibleAfterLoad)
        .then(obs => obs.subscribe())
    ]);
  }

  // ============================================================================
  // SYNC STATE CHANGE HANDLING
  // ============================================================================

  private async onFaultsSyncStateChanged(params: {
    changes: SyncStateChange[];
    startingFaultUids: string[];
    notifyError: boolean;
    modifiedOn?: Date;
    modifier?: User;
  }): Promise<void> {
    const categorized = this.categorizeSyncChanges(params.changes);

    // Handle syncing state
    categorized.syncing.forEach(s => this.startPending(s.faultUid));

    // Handle errors
    if (categorized.error.length > 0) {
      await this.handleSyncErrors(categorized.error, params);
    }

    // Handle synced
    if (categorized.synced.length > 0) {
      await this.handleSyncedFaults(categorized.synced, params);
    }
  }

  /** Categorize sync changes by status */
  private categorizeSyncChanges(changes: SyncStateChange[]): CategorizedSyncChanges {
    return {
      syncing: changes.filter(x => x.syncState?.status === SyncStatusEnum.Syncing),
      error: changes.filter(x => x.syncState?.status === SyncStatusEnum.Error),
      synced: changes.filter(x => x.syncState?.status === SyncStatusEnum.Synced)
    };
  }

  /** Handle sync errors */
  private async handleSyncErrors(
    errors: SyncStateChange[],
    params: { startingFaultUids: string[]; notifyError: boolean }
  ): Promise<void> {
    let hasInternalErrors = false;

    for (const e of errors) {
      if (e.syncState?.error) {
        hasInternalErrors = await this.processErrorNotifications(e, params) || hasInternalErrors;
      }
    }

    if (!hasInternalErrors && params.notifyError) {
      this.logger.error('MM.fault.syncError', '', 'MmFaultsSyncService');
      this.notificationService.error(
        this.translateService.instant('MM.fault.faultChangeError'),
        this.translateService.instant('MM.fault.syncError')
      );
    }

    // Stop pending and sync
    errors.forEach(s => this.stopPending(s.faultUid));

    if (errors[0]?.mydocmaUid === this.project?.MyDocmaUid) {
      await this.faultsSynced(
        this.project.MyDocmaUid,
        this.extractUids(errors, 'faultUid'),
        this.extractUids(errors, 'groupUid'),
        this.extractUids(errors, 'attachmentUid')
      );
    }
  }

/** Process error notifications and return whether internal errors were found */
  private async processErrorNotifications(
    errorChange: SyncStateChange,
    params: { startingFaultUids: string[]; notifyError: boolean }
  ): Promise<boolean> {
    try {
      const error = this.errorService.getError(errorChange.syncState?.error);
      if (!error?.InternalErrors) {
        return false;
      }

      const shouldNotify = params.notifyError ||
        params.startingFaultUids?.includes(errorChange.faultUid);

      for (const errorKey in error.InternalErrors) {
        if (error.InternalErrors.hasOwnProperty(errorKey)) {
          const internalError = error.InternalErrors[errorKey] as { Prop: string; Message: string };
          if (shouldNotify && internalError?.Prop && internalError?.Message) {
            const message = this.formatErrorMessage(internalError);
            this.logger.error(message, '', 'MmFaultsSyncService');
            this.notificationService.error(message, this.translateService.instant('MM.fault.faultChangeError'));
          }
        }
      }
      return true;
    } catch {
      return false;
    }
  }

  /** Format error message with field names */
  private formatErrorMessage(internalError: { Prop: string; Message: string }): string {
    const fieldName = this.fieldMappings.byDefaultName[internalError.Prop]
      ?? this.fieldMappings.byProp[internalError.Prop];

    if (fieldName) {
      return internalError.Message.replaceAll(`'${internalError.Prop}'`, `'${fieldName}'`);
    }
    return internalError.Message;
  }

  /** Handle synced faults */
  private async handleSyncedFaults(
    synced: SyncStateChange[],
    params: { modifiedOn?: Date; modifier?: User }
  ): Promise<void> {
    synced.forEach(s => this.stopPending(s.faultUid));

    if (synced[0]?.mydocmaUid === this.project?.MyDocmaUid) {
      await this.faultsSynced(
        this.project.MyDocmaUid,
        synced.map(x => x.faultUid),
        this.extractUids(synced, 'groupUid'),
        this.extractUids(synced, 'attachmentUid'),
        params.modifiedOn,
        params.modifier
      );
    }
  }

  /** Extract and filter UIDs from sync changes */
  private extractUids(changes: SyncStateChange[], key: 'faultUid' | 'groupUid' | 'attachmentUid'): string[] {
    return changes.map(x => x[key]).filter(Boolean);
  }

  // ============================================================================
  // PENDING STATE MANAGEMENT
  // ============================================================================

  private stopPending(faultUid: string): void {
    const subject = this.syncFaultSubjectsMap.get(faultUid);
    if (subject) {
      subject.next(false);
      this.pendingService.removePendingState(subject);
    }
  }

  private startPending(faultUid: string): void {
    let subject = this.syncFaultSubjectsMap.get(faultUid);
    if (!subject) {
      subject = new BehaviorSubject<boolean>(true);
      this.syncFaultSubjectsMap.set(faultUid, subject);
    }
    subject.next(true);
    this.pendingService.registerPendingState(subject);
  }

  // ============================================================================
  // FAULT SYNCED PROCESSING
  // ============================================================================

  private async faultsSynced(
    mydocmaUid: string,
    faultUids: string[],
    groupUids: string[],
    attachmentUids: string[],
    modifiedOn?: Date,
    modifier?: User
  ): Promise<void> {
    const viewFaults = this.faultsLoaderService.getFaults(faultUids);
    if (!viewFaults?.length) {
      return;
    }

    const saved = await this.faultService.getFaults(mydocmaUid, viewFaults, faultUids, groupUids, attachmentUids);

    // Process faults in parallel where possible
    await Promise.all(saved.faults.map(fault =>
      this.processSyncedFault(mydocmaUid, fault, viewFaults, saved.removedFromIDb, modifiedOn, modifier)
    ));

    // Load layout faults
    const faultsWithId = viewFaults.filter(x => !!x.id);
    if (faultsWithId.length > 0) {
      (await this.faultsLoaderService.loadLayoutFaults(faultsWithId, true, true, LoadLayoutFaultsOptions.EnsureVisibleAfterLoad))
        .subscribe();
    }
  }

  /** Process a single synced fault */
  private async processSyncedFault(
    mydocmaUid: string,
    fault: Fault,
    viewFaults: ViewFault[],
    removedFromIDb: string[] | undefined,
    modifiedOn?: Date,
    modifier?: User
  ): Promise<void> {
    // Update modified timestamp if needed
    if (this.shouldUpdateModifiedTimestamp(fault, modifiedOn, modifier)) {
      this.updateFaultTimestamp(fault, modifiedOn, modifier);

      if (!removedFromIDb?.includes(fault.uid)) {
        await this.persistenceService.save(mydocmaUid, fault);
      }
    }

    const viewFault = viewFaults.find(x => x.uid === fault.uid);
    if (!viewFault) {
      return;
    }

    if (viewFault.unsavedNew) {
      await this.processNewFaultSynced(mydocmaUid, viewFault, fault);
    } else {
      this.processExistingFaultSynced(mydocmaUid, viewFault, fault, modifiedOn, modifier);
    }
  }

  /** Check if fault timestamp should be updated */
  private shouldUpdateModifiedTimestamp(fault: Fault, modifiedOn?: Date, modifier?: User): boolean {
    return !!(
      modifiedOn &&
      modifier &&
      fault.data &&
      Object.keys(fault.data).length > 0 &&
      (!fault.data.ModifiedOn || fault.data.ModifiedOn < modifiedOn)
    );
  }

  /** Update fault timestamps */
  private updateFaultTimestamp(fault: Fault, modifiedOn: Date, modifier: User): void {
    fault.data.ModifiedOn = modifiedOn;
    fault.data.Modifier = modifier;

    if (fault.loadState.data && Object.keys(fault.loadState.data).length > 0) {
      fault.loadState.data.ModifiedOn = modifiedOn;
      fault.loadState.data.Modifier = modifier;
    }
  }

  /** Process newly created fault after sync */
  private async processNewFaultSynced(mydocmaUid: string, viewFault: ViewFault, fault: Fault): Promise<void> {
    viewFault.id = fault.id;
    viewFault.unsavedNew = false;
    viewFault.bpPin = fault.data?.Pin;
    viewFault.data.Pin = fault.data?.Pin;

    if (fault.loadState.data) {
      viewFault.loadState = fault.loadState;
    }
    viewFault.changeState = fault.changeState;
    viewFault.syncState = fault.syncState;

    this.mergeAttachments(mydocmaUid, viewFault, fault.attachments);
    this.mergeActivities(mydocmaUid, viewFault, fault.activities);

    if (viewFault.id) {
      await this.faultsLoaderService.loadFaults([viewFault], true).toPromise();
    }

    // Handle text pin if enabled
    await this.updateTextPinIfEnabled(viewFault);

    this.syncNewFaultSubject.next(viewFault);
    this.faultService.faultChangedSubject.next(viewFault);
  }

  /** Update text pin if feature is enabled */
  private async updateTextPinIfEnabled(viewFault: ViewFault): Promise<void> {
    if (!this.project?.textPinEnabled || !this.project?.textPinFieldId) {
      return;
    }

    try {
      const fields = await firstValueFrom(
        this.projectLoaderService.fields.pipe(
          filter(f => f.loadState.status === LoadStatusEnum.Loaded),
          map(m => m.data.filter(x =>
            x.Id != null &&
            [FieldPropertyEnum.Number1, FieldPropertyEnum.Number2, FieldPropertyEnum.Desc1_1].includes(x.Prop as FieldPropertyEnum)
          ))
        )
      );

      const fieldDict = Object.fromEntries(fields.map(x => [x.Prop, x]));

      if (fieldDict[FieldPropertyEnum.Number1]?.FieldId === +this.project.textPinFieldId) {
        viewFault.pinText = String(viewFault.data.Number1);
      }
    } catch {
      // Ignore errors in text pin update
    }
  }

  /** Process existing fault after sync */
  private processExistingFaultSynced(
    mydocmaUid: string,
    viewFault: ViewFault,
    fault: Fault,
    modifiedOn?: Date,
    modifier?: User
  ): void {
    viewFault.bpPin = fault.data?.Pin;
    viewFault.data.Pin = fault.data?.Pin;

    if (fault.loadState.data) {
      viewFault.loadState = fault.loadState;
    } else if (viewFault.loadState.data && Object.keys(viewFault.loadState.data).length > 0 && modifiedOn) {
      viewFault.loadState.data.ModifiedOn = new Date(modifiedOn);
      viewFault.loadState.data.Modifier = modifier;
    }

    viewFault.changeState = fault.changeState;
    viewFault.syncState = fault.syncState;

    this.mergeAttachments(mydocmaUid, viewFault, fault.attachments);
    this.mergeActivities(mydocmaUid, viewFault, fault.activities);

    this.faultService.faultChangedSubject.next(viewFault);
    this.syncFaultSubject.next(viewFault);
  }

  // ============================================================================
  // MERGE OPERATIONS
  // ============================================================================

  private mergeAttachments(
    mydocmaUid: string,
    model: { attachments: Attachment[]; addAttachment(a: Attachment): void },
    attachments: Attachment[]
  ): void {
    // Update existing attachments
    for (const attachment of model.attachments) {
      const syncedAttachment = attachments.find(a => a.uid === attachment.uid);
      if (syncedAttachment) {
        this.updateAttachmentFromSynced(mydocmaUid, attachment, syncedAttachment);
      }
    }

    // Add new attachments
    for (const attachment of attachments) {
      if (!model.attachments.some(a => a.uid === attachment.uid)) {
        model.addAttachment(attachment);
      }
    }
  }

  /** Update attachment from synced data */
  private async updateAttachmentFromSynced(
    mydocmaUid: string,
    attachment: Attachment,
    syncedAttachment: Attachment
  ): Promise<void> {
    const isNew = !attachment.id;

    attachment.loadState = syncedAttachment.loadState;
    attachment.changeState = syncedAttachment.changeState;
    attachment.syncState = syncedAttachment.syncState;

    // Load PDF preview for new attachments
    if (isNew && attachment.data.MimeType === 'application/pdf') {
      attachment.preview = new AttachmentSource();
      attachment.thumbnail = new AttachmentSource();

      await Promise.all([
        this.attachmentService.loadAttachmentPreview(mydocmaUid, attachment, null, AttachmentLoadingTypeEnum.FromServer, true),
        this.attachmentService.loadAttachmentThumbnail(mydocmaUid, attachment, null, AttachmentLoadingTypeEnum.FromDb, true)
      ]);
    }

    // Merge scribbles
    attachment.scribbles = attachment.scribbles.filter(s =>
      syncedAttachment.scribbles.some(x => x.uid === s.uid)
    );

    for (const scribble of attachment.scribbles) {
      const syncedScribble = syncedAttachment.scribbles.find(x => x.uid === scribble.uid);
      if (syncedScribble) {
        scribble.loadState = syncedScribble.loadState;
        scribble.changeState = syncedScribble.changeState;
        scribble.syncState = syncedScribble.syncState;
      }
    }
  }

  private mergeActivities(mydocmaUid: string, model: Fault, activities: Activity[]): void {
    // Update existing activities
    for (const activity of model.activities) {
      const syncedActivity = activities.find(a => a.uid === activity.uid);
      if (syncedActivity) {
        activity.loadState = syncedActivity.loadState;
        activity.changeState = syncedActivity.changeState;
        activity.syncState = syncedActivity.syncState;
        this.mergeAttachments(mydocmaUid, activity, syncedActivity.attachments);
      }
    }

    // Add new activities
    for (const activity of activities) {
      if (!model.activities.some(a => a.uid === activity.uid)) {
        model.addActivity(activity);
      }
    }
  }

  // ============================================================================
  // ATTACHMENT CLEANUP
  // ============================================================================

  private async cleanupPendingAttachmentDeletes(fault: ViewFault): Promise<void> {
    const allAttachments = [
      ...fault.attachments.map(a => ({ attachment: a, activity: null as Activity })),
      ...fault.activities.flatMap(act =>
        act.attachments.map(a => ({ attachment: a, activity: act }))
      )
    ];

    const deletePromises: Promise<boolean>[] = [];
    const attachmentsToCheck: Array<{ attachment: Attachment; activity: Activity | null }> = [];

    // Collect pending deletes
    for (const item of allAttachments) {
      if (this.attachmentService.isPendingDelete(item.attachment.uid) && item.attachment.id) {
        attachmentsToCheck.push(item);
        deletePromises.push(
          this.attachmentService.completePendingDelete(item.attachment, fault, item.activity)
        );
      }
    }

    if (deletePromises.length === 0) {
      return;
    }

    // Process deletes in parallel
    const results = await Promise.all(deletePromises);
    let hasDeletes = false;

    for (let i = 0; i < results.length; i++) {
      if (results[i]) {
        const { attachment, activity } = attachmentsToCheck[i];
        if (activity) {
          fault.removeAttachmentFromActivity(attachment, activity);
        } else {
          fault.removeAttachment(attachment);
        }
        hasDeletes = true;
      }
    }

    if (hasDeletes) {
      await Promise.all([
        this.faultsLoaderService.loadFaults([fault], true).toPromise(),
        this.faultsLoaderService.loadLayoutFaults([fault], true, true).then(obs => obs.subscribe())
      ]);
    }
  }

}
